import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, CopyText, Icon, TextArea } from '@buderus-apps/components'
import { usePostFeedback } from "hooks";
import styles from './styles.module.scss'

const {
  feedback,
  feedbackForm,
  feedbackRating,
  feedbackStar,
  feedbackStarIsActive,
  feedbackTextareaWrapper,
  feedbackTextarea,
  feedbackButton,
  feedbackNotification
} = styles

type TProps = {
  question: string,
  stars?: number,
  conversationId: string,
  siteId: string
}

const Feedback: FC<TProps> = (props) => {
  const { question, stars = 0, conversationId, siteId } = props
  const { t } = useTranslation()

  const feedbackRef = useRef<HTMLDivElement>(null)

  const [rating, setRating] = useState<number>(0);
  const [text, setText] = useState<string>('');

  const { postFeedbackData, isPostFeedbackLoading, postFeedback, postFeedbackErrorData } = usePostFeedback()

  const handleClickRating = useCallback((star: number) => {
    if (!isPostFeedbackLoading && !postFeedbackData) {
      setRating(star)
    }
  }, [isPostFeedbackLoading, postFeedbackData])

  const handleTextareaChange = (event: React.ChangeEvent<HTMLTextAreaElement> | React.FormEvent<HTMLInputElement>) => {
    const target = event.target as HTMLTextAreaElement | HTMLInputElement;
    setText(target.value);
  }

  useEffect(() => {
    if (feedbackRef?.current && rating > 0) {
      feedbackRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [rating])

  const onSubmit = useCallback(async (event: any) => {
    event.preventDefault()
    event.stopPropagation()
    await postFeedback({
      star: rating,
      text,
      question,
      contact: '',
      siteId,
      conversationId
    })
    feedbackRef?.current?.scrollIntoView()
  }, [text, rating, question, postFeedback, feedbackRef, siteId, conversationId])

  return (
    <div ref={feedbackRef} className={feedback}>
      <div className={feedbackForm}>
        <CopyText type='medium-small'>{t('DOES_THIS_ANSWER_HELP')}</CopyText>
        <div className={feedbackRating}>
          {[1, 2, 3, 4, 5].map((star) => {
            const value = rating || stars
            return <Icon key={star} name='Star' className={`${feedbackStar} ${star <= value && feedbackStarIsActive}`}
                  onClick={() => handleClickRating(star)}/>
          })}
        </div>

        {(!postFeedbackData && rating > 0) && (
          <>
            <div className={feedbackTextareaWrapper}>
              <TextArea
                className={feedbackTextarea}
                label={t('WITH_YOUR_FEEDBACK_WE_IMPROVE')}
                placeholder={t('FEEDBACK_INPUT_PLACEHOLDER')}
                onChange={(event) => handleTextareaChange(event)}
              />
            </div>

            <Button className={feedbackButton} btnStyle={'cta'} onClick={(event) => onSubmit(event)}>
              {isPostFeedbackLoading ? t('APP.SEND') : t('APP.SEND_FEEDBACK')}
            </Button>
          </>
        )}
      </div>

      {postFeedbackData && (
        <div className={feedbackNotification}>
          <Icon.Success/>
          <CopyText type='medium'>{t('SUCCESS_POST_FEEDBACK')}</CopyText>
        </div>
      )}

      {postFeedbackErrorData && (
        <div className={feedbackNotification}>
          <Icon.Caution/>
          <CopyText type='medium'>{t('ERROR_POST_FEEDBACK')}</CopyText>
        </div>
      )}
    </div>
  )
}

export default React.memo(Feedback)
